import { useEffect, useState } from "react";
import { NavLink } from "react-router-dom";
import axios from "axios";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { Message, Popconfirm } from "@arco-design/web-react";
import { IconQuestionCircle } from "@arco-design/web-react/icon";

import { endpoints } from "../../api/endpoints";
import { routes } from "../../routes/routes";
import { Meeting } from "../../models/Meeting";
import { RootState } from "../../store/store";
import {
  getInitials,
  getInitialsFromFullName,
  isDefaultOrMissingAvatar,
} from "../../utils/avatar";

import "./HomeMeetingCard.css";

type Props = {
  meeting: Meeting;
  onCancelMeeting: () => void;
};

function splitDisplayName(fullName: string): { firstName: string; lastName: string } {
  const parts = fullName.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return { firstName: "", lastName: "" };
  if (parts.length === 1) return { firstName: parts[0], lastName: "" };
  return { firstName: parts[0], lastName: parts.slice(1).join(" ") };
}

function formatMeetingTime(displayTime?: string, startTime?: string): string {
  const value = displayTime || startTime || "";
  if (!value) return "";
  // Backend: "Thursday 05/28/2026 02:00 PM" → mock-style "Thursday 05/28/2026 at 02:00 PM"
  const match = value.match(/^(.+?\d{4})\s+(\d{1,2}:\d{2}\s*[AP]M)$/i);
  if (match) {
    return `${match[1]} at ${match[2]}`;
  }
  return value;
}

export function HomeMeetingCard({ meeting, onCancelMeeting }: Props) {
  const { t } = useTranslation();
  const user = useSelector((state: RootState) => state.user.user);

  const {
    mentor_user_id,
    mentee_user_id,
    mentee_name,
    mentor_name,
    id,
    display_time,
    start_time,
    mentee_avatar_url,
    mentor_avatar_url,
  } = meeting;

  const isAdminViewer = !!user?.isAdmin;
  const isViewerMentor = !isAdminViewer && Number(mentor_user_id) === Number(user?.id);

  const otherPersonId = isAdminViewer
    ? mentor_user_id || 0
    : isViewerMentor
      ? mentee_user_id || 0
      : mentor_user_id || 0;
  const otherPersonName = isAdminViewer
    ? `${mentor_name} & ${mentee_name}`
    : isViewerMentor
      ? mentee_name
      : mentor_name;
  const otherPersonAvatar = isAdminViewer
    ? mentor_avatar_url
    : isViewerMentor
      ? mentee_avatar_url
      : mentor_avatar_url;
  const { firstName, lastName } = splitDisplayName(otherPersonName);
  const initials =
    firstName || lastName
      ? getInitials(firstName, lastName)
      : getInitialsFromFullName(otherPersonName);

  const [avatarFailed, setAvatarFailed] = useState(false);
  const [profileRoute, setProfileRoute] = useState(
    isAdminViewer ? routes.MENTOR : isViewerMentor ? routes.MENTEE : routes.MENTOR
  );

  const avatarSrc =
    !avatarFailed && !isDefaultOrMissingAvatar(otherPersonAvatar)
      ? otherPersonAvatar
      : undefined;

  useEffect(() => {
    setAvatarFailed(false);
  }, [otherPersonAvatar, otherPersonId]);

  useEffect(() => {
    if (!otherPersonId) return;
    axios
      .get(`${endpoints.getPublicProfile}${otherPersonId}`)
      .then((response) => {
        setProfileRoute(response.data.isMentor ? routes.MENTOR : routes.MENTEE);
      })
      .catch((error) => {
        console.error("Error fetching profile data:", error);
      });
  }, [otherPersonId, isViewerMentor]);

  return (
    <article className="home-meeting-card">
      <div className="home-meeting-card__content">
        <div className="home-meeting-card__avatar">
          {avatarSrc ? (
            <img
              className="home-meeting-card__avatar-img"
              src={avatarSrc}
              alt={otherPersonName}
              referrerPolicy="no-referrer"
              onError={() => setAvatarFailed(true)}
            />
          ) : (
            <span className="home-meeting-card__avatar-initials" aria-hidden="true">
              {initials}
            </span>
          )}
        </div>

        <h3 className="home-meeting-card__title">
          {t("home_page.meeting_with", { otherPersonName })}
        </h3>
        <p className="home-meeting-card__time">
          {formatMeetingTime(display_time, start_time)}
        </p>
      </div>

      <div
        className={`home-meeting-card__actions${isAdminViewer ? " home-meeting-card__actions--admin" : ""}`}
      >
        {!isAdminViewer && (
          <div className="home-meeting-card__action-item">
            <Popconfirm
              title={t("home_page.cancel_meeting_confirmation_message")}
              icon={<IconQuestionCircle />}
              onOk={() => {
                axios
                  .post(`${endpoints.cancelMeeting}`, { meeting_id: id })
                  .then(() => {
                    onCancelMeeting();
                    Message.success({
                      content: t("notification_messages.meeting_canceled_success"),
                      closable: true,
                      duration: 3000,
                    });
                  })
                  .catch((error) => {
                    console.error(error);
                    Message.error(t("notification_messages.meeting_canceled_error"));
                  });
              }}
              okText={t("common.confirm_cancel_button")}
              cancelText={t("common.decline_cancel_button")}
            >
              <button type="button" className="home-meeting-card__btn home-meeting-card__btn--secondary">
                {t("common.cancel_button")}
              </button>
            </Popconfirm>
          </div>
        )}

        <NavLink
          className="home-meeting-card__btn home-meeting-card__btn--primary"
          to={profileRoute.replace(":id", otherPersonId.toString())}
        >
          {t("common.view_mentor_profile_button")}
        </NavLink>
      </div>
    </article>
  );
}
